import { ErrorMessage } from '@hookform/error-message'
import {
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
} from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Modal from '@material-ui/core/Modal'
import Paper from '@material-ui/core/Paper'
import { makeStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import LoadingButton from 'src/components/ui/Button/LoadingButton'
import { updateUserInfo } from 'src/redux/slices/settings'

const useStyles = makeStyles((theme) => ({
	saveModal: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	saveModalContent: {
		borderRadius: '10px',
		backgroundColor: theme.palette.background.paper,
		width: '70%',
	},
	formContainer: {
		padding: theme.spacing(3),
	},
	buttonsContainer: {
		flexGrow: 1,
		marginLeft: 'auto',
	},
	button: {
		padding: theme.spacing(1),
	},
	errorMsg: {
		color: 'red',
		padding: 0,
		'&:before': {
			display: 'inline',
			content: '"⚠"',
		},
	},
	fields: {
		display: 'flex',
		'& div': {
			marginRight: '1rem',
		},
	},
}))

function UserProfileModal({ isOpen, closeUserProfileModal }) {
	const classes = useStyles()
	const dispatch = useDispatch()
	const { t } = useTranslation()
	const { t: tSettings } = useTranslation('settings')
	const { t: tLanguages } = useTranslation('languages')
	const { t: tProjectChildren } = useTranslation('projectChildren')
	const { t: tClientFormModal } = useTranslation('ClientFormModal')
	const { register, handleSubmit, errors, control } = useForm({ mode: 'all' })

	const authInfo = useSelector((state) => state.auth.userInfo)
	const userInformation = useSelector(
		(state) => state.settings.userInformation
	)
	console.log('userInformation: ', userInformation)
	const updateUserInfoApiStatus = useSelector(
		(state) => state.settings.updateUserInfoApiStatus
	)
	const createUserInfoApiStatus = useSelector(
		(state) => state.settings.createUserInfoApiStatus
	)

	const [userLanguage, setUserLanguage] = useState(
		userInformation?.userLanguage ?? 'fr'
	)

	const UserProfileSubmit = (data) => {
		dispatch(updateUserInfo(data))
	}

	const handleCloseModal = () => {
		closeUserProfileModal()
	}
	function handleLanguageDropdownChange(event) {
		const userLanguage = event.target.value
		setUserLanguage(userLanguage)
		register({ name: 'userLanguage', value: userLanguage })
	}

	const languageArray = [
		{ value: 'en', label: tLanguages('english') },
		{ value: 'fr', label: tLanguages('french') },
	]

	return (
		<Modal
			aria-labelledby="User Profile Modal"
			aria-describedby="Add or Edit User details"
			open={isOpen}
			className={classes.saveModal}
			onClose={handleCloseModal}
		>
			<div className={classes.saveModalContent}>
				{userInformation && (
					<Grid container>
						<Paper className={classes.formContainer}>
							<form
								onSubmit={handleSubmit(UserProfileSubmit)}
								noValidate
							>
								<Grid item container spacing={2}>
									<Grid item xs={12}>
										<Typography variant="h4">
											{tSettings('userProfile')}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<TextField
											variant="outlined"
											margin="normal"
											inputRef={register({
												required: true,
											})}
											required
											fullWidth
											id="firstName"
											label={tSettings('firstName')}
											name="firstName"
											defaultValue={
												userInformation?.firstName
													? userInformation.firstName
													: null
											}
											helperText={
												errors.firstName ? (
													<>
														{errors.firstName
															.type ===
															'required' && (
															<span
																className={
																	classes.errorMsg
																}
															>
																{tSettings(
																	'errors.firstName'
																)}
															</span>
														)}
													</>
												) : (
													tSettings('firstName')
												)
											}
											autoFocus
										/>
									</Grid>
									<Grid item xs={6}>
										<TextField
											variant="outlined"
											margin="normal"
											inputRef={register({
												required: true,
											})}
											required
											fullWidth
											id="lastName"
											label={tSettings('lastName')}
											name="lastName"
											defaultValue={
												userInformation?.lastName
													? userInformation.lastName
													: null
											}
											helperText={
												errors.lastName ? (
													<>
														{errors.lastName
															.type ===
															'required' && (
															<span
																className={
																	classes.errorMsg
																}
															>
																{tSettings(
																	'errors.lastName'
																)}
															</span>
														)}
													</>
												) : (
													tSettings('lastName')
												)
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<TextField
											variant="outlined"
											margin="normal"
											inputRef={register({
												required: true,
											})}
											required
											disabled
											fullWidth
											id="email"
											label={tSettings('email')}
											name="email"
											defaultValue={
												userInformation?.email ?? null
											}
											helperText={
												errors.email ? (
													<>
														{errors.email.type ===
															'required' && (
															<span
																className={
																	classes.errorMsg
																}
															>
																{tSettings(
																	'errors.email'
																)}
															</span>
														)}
													</>
												) : (
													tSettings('email')
												)
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<TextField
											variant="outlined"
											margin="normal"
											inputRef={register}
											fullWidth
											id="phone"
											label={tSettings('phone')}
											name="phone"
											defaultValue={
												userInformation?.phone
													? userInformation.phone
													: null
											}
											helperText={tSettings('phone')}
										/>
									</Grid>
									<Grid item xs={12} md={6}>
										<FormControl
											variant="outlined"
											required
											style={{ width: '100%' }}
										>
											<InputLabel
												id={'userLanguage'}
												required={true}
											>
												{tSettings('appLanguage')}
											</InputLabel>
											<Controller
												control={control}
												name={'userLanguage'}
												rules={{ required: true }}
												defaultValue={
													userInformation?.userLanguage ??
													'fr'
												}
												as={
													<Select
														name={'userLanguage'}
														labelId={'userLanguage'}
														required={true}
														value={userLanguage}
														onChange={
															handleLanguageDropdownChange
														}
														label={tSettings(
															'appLanguage'
														)}
													>
														{languageArray.map(
															(language) => (
																<MenuItem
																	key={
																		language.value
																	}
																	className={
																		classes.options
																	}
																	value={
																		language.value
																	}
																>
																	{
																		language.label
																	}
																</MenuItem>
															)
														)}
													</Select>
												}
											/>
											<ErrorMessage
												errors={errors}
												name={'userLanguage'}
												render={() => (
													<p style={{ color: 'red' }}>
														{tProjectChildren(
															'addQuoteModal.sections.sectionTwoPhotoGrid.errorMsg.required'
														)}
													</p>
												)}
											/>
										</FormControl>

										{errors.blankQuoteType?.type ===
											'required' && (
											<FormHelperText>
												<span
													className={classes.errorMsg}
												>
													{tProjectChildren(
														'addQuoteModal.sections.sectionTwoPhotoGrid.errorMsg.required'
													)}
												</span>
											</FormHelperText>
										)}
									</Grid>

									<Grid
										item
										xs={12}
										className={classes.buttonsContainer}
									>
										<span className={classes.button}>
											<LoadingButton
												type="submit"
												variant="contained"
												color="primary"
												className={classes.button}
												loading={
													updateUserInfoApiStatus ===
													'loading'
														? true
														: false
												}
											>
												{tClientFormModal('save')}
											</LoadingButton>
										</span>
										<span className={classes.button}>
											<Button
												variant="contained"
												color="secondary"
												className={classes.button}
												onClick={handleCloseModal}
											>
												{tClientFormModal('cancel')}
											</Button>
										</span>
									</Grid>
								</Grid>
							</form>
						</Paper>
					</Grid>
				)}
			</div>
		</Modal>
	)
}

export default UserProfileModal
